import React from "react";
import Autosuggest from "react-autosuggest";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled, { css } from "styled-components";

import { IStyle } from "../../../../Types/Style";
import { StyledAutosuggestContainer } from "../../../../components/pages/CourseAuthor/Autocomplete";
import colors from "../../../../styles/colors";
import fontSizes from "../../../../styles/fontSizes";
import { contains } from "../../../../utils";
import { IState } from "../../../reducer";
import { IFontFamily, RequestState } from "../../../types/common";
import * as actions from "../../actions";
import { Section } from "../../components/common/containers";

interface IFontsDispatchProps {
  applyFontToSection: (section: actions.FontSection, fontName: IFontOption, style: IStyle) => void;
}

interface IFontsStateProps {
  isLoading: boolean;
  orgFontFamilies: IFontFamily[];
}

interface IFontsOwnProps {
  style: IStyle;
}

type FontsProps = IFontsOwnProps & IFontsStateProps & IFontsDispatchProps;

interface IFontSectionProps {
  currentFontFamilyId: string | null;
  sectionName: string;
  myFonts: IFontOption[];
  selectFont: (fontName: IFontOption) => void;
}

interface IFontOption {
  name: string;
  ffmId: string | null;
}

interface IFontSection {
  title: string;
  fonts: IFontOption[];
}

const mkFontOption = (name: string): IFontOption => ({ name, ffmId: null });
const customStyles = css`
  width: 50%;

  .react-autosuggest__suggestions-list {
    margin: 0.5rem 0;
  }
`;

const AutosuggestSection = styled.strong`
  padding-left: 1rem;
`;

const renderSuggestion = (suggestion: IFontOption) => <span>{suggestion.name}</span>;

const renderSectionTitle = (section: IFontSection) => <AutosuggestSection>{section.title}</AutosuggestSection>;

export const FontSection: React.FC<IFontSectionProps> = props => {
  const allFonts: IFontSection[] = [
    {
      fonts: props.myFonts,
      title: "My Organisation Fonts",
    },
    {
      fonts: [
        mkFontOption("Roboto"),
        mkFontOption("Open Sans"),
        mkFontOption("Lato"),
        mkFontOption("Montserrat"),
        mkFontOption("Nunito"),
        mkFontOption("Merriweather"),
      ],
      title: "Google Fonts",
    },
  ];
  const [inputValue, setInputValue] = React.useState("");
  const [suggestions, setSuggestions] = React.useState<IFontSection[]>([]);
  const setSelected = (
    _: React.FormEvent<HTMLElement>,
    { suggestion }: Autosuggest.SuggestionSelectedEventData<IFontOption>
  ) => {
    props.selectFont(suggestion);
    setInputValue("");
  };
  const getSectionSuggestions = (section: IFontSection) => section.fonts;
  const getSuggestionValue = (suggestion: IFontOption) => suggestion.name;
  const onSuggestionsFetchRequested = () => {
    setSuggestions(
      allFonts
        .map(section => ({
          ...section,
          fonts: section.fonts.filter(font => contains(inputValue, font.name)),
        }))
        .filter(section => section.fonts.length > 0)
    );
  };
  const onSuggestionsClearRequested = () => setSuggestions([]);
  const inputProps = {
    onChange: (_: React.FormEvent<HTMLElement>, { newValue }: Autosuggest.ChangeEvent) => setInputValue(newValue),
    placeholder: `Type here to look for your fonts for ${props.sectionName}`,
    style: {
      border: `0.1em solid ${colors.grey}`,
      borderRadius: "0.5rem",
      fontSize: `${fontSizes.regular}`,
      marginBottom: "0.5rem",
      padding: "0.5rem",
      width: "50%",
    },
    value: inputValue,
  };
  const currentFontFamilyName = props.myFonts.find(font => font.ffmId === props.currentFontFamilyId);
  return (
    <StyledAutosuggestContainer customStyles={customStyles}>
      <h4>{props.sectionName}</h4>
      <p>
        Currently selected: <strong>{currentFontFamilyName?.name ?? "use default"}</strong>
      </p>
      <Autosuggest<IFontOption, IFontSection>
        multiSection={true}
        suggestions={suggestions}
        inputProps={inputProps}
        getSuggestionValue={getSuggestionValue}
        getSectionSuggestions={getSectionSuggestions}
        renderSuggestion={renderSuggestion}
        renderSectionTitle={renderSectionTitle}
        onSuggestionSelected={setSelected}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      />
    </StyledAutosuggestContainer>
  );
};

export const Fonts: React.FC<FontsProps> = props => {
  const { style } = props;
  const selectFont = (fontType: actions.FontSection) => (fontOption: IFontOption) => {
    props.applyFontToSection(fontType, fontOption, props.style);
  };
  const myFonts = props.orgFontFamilies.map(({ displayName, ffmId }) => ({
    ffmId,
    name: displayName,
  }));
  if (props.isLoading) {
    return (
      <Section>
        <span>Loading fonts...</span>
      </Section>
    );
  }
  return (
    <Section>
      <h2>Fonts</h2>
      <FontSection
        currentFontFamilyId={style.headerFontFamilyId}
        sectionName="Header"
        myFonts={myFonts}
        selectFont={selectFont(actions.FontSection.HEADER)}
      />
      <FontSection
        currentFontFamilyId={style.bodyFontFamilyId}
        sectionName="Body"
        myFonts={myFonts}
        selectFont={selectFont(actions.FontSection.BODY)}
      />
      <FontSection
        currentFontFamilyId={style.monospaceFontFamilyId}
        sectionName="Monospace"
        myFonts={myFonts}
        selectFont={selectFont(actions.FontSection.MONOSPACE)}
      />
    </Section>
  );
};

const mapStateToProps = (state: IState): IFontsStateProps => ({
  isLoading:
    state.App.ThemeManagement.applyFontToSectionStatus === RequestState.PENDING ||
    state.App.ThemeManagement.loadOrgFontFamiliesState === RequestState.PENDING,
  orgFontFamilies: state.App.ThemeManagement.orgFontFamilies,
});

const mapDispatchToProps = (dispatch: Dispatch): IFontsDispatchProps => ({
  applyFontToSection: (section: actions.FontSection, fontOption: IFontOption, style: IStyle) =>
    dispatch(actions.applyFontToSection({ fontOption, section, style })),
});

export default connect(mapStateToProps, mapDispatchToProps)(Fonts);
